import { isSubscriptionExpired } from "@/hooks/utils";
import { CircleCheckBig, CircleX } from "lucide-react";
import { useCallback, useEffect } from "react";
import { useNavigate } from "react-router";
import { useAppDispatch, useAppSelector } from "../../store/hooks";
import { pushModal } from "../../store/slices/modalsSlice";
import GeneralCTAModal from "../Modals/GeneralCTAModal";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "../ui/accordion";
import { Badge } from "../ui/badge";
import { Button } from "../ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "../ui/card";
import styles from "./LandingPage.module.css";
import { faq, features, plans } from "./LandingPageData";

const LandingPage = () => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();

  const { userProfileData } = useAppSelector((state) => state.store);

  useEffect(() => {
    var prevScrollpos = window.pageYOffset;
    window.onscroll = function () {
      var currentScrollPos = window.pageYOffset;
      if (prevScrollpos > currentScrollPos) {
        document
          .querySelector("#navbar")
          ?.classList?.remove("hide-qashqool-nav");
      } else {
        document.querySelector("#navbar")?.classList?.add("hide-qashqool-nav");
      }
      prevScrollpos = currentScrollPos;
    };
  }, []);

  const registerPopup = useCallback(() => {
    dispatch(
      pushModal(
        <GeneralCTAModal
          closeOnClick={true}
          msg="يجب الإشتراك أولاً لشراء هذه الخطة"
          yesMsg="اشترك مجاناً"
          noMsg="لاحقاً"
          yesHandler={() => navigate("/registration")}
        />
      )
    );
  }, []);

  return (
    <>
      <nav
        id="navbar"
        className="sticky top-0 z-[999] bg-white shadow-sm transition-all"
      >
        <div className="container mx-auto flex flex-wrap items-center justify-between px-4 py-2">
          <a href="#" className="flex items-center">
            <img
              height="40"
              width="40"
              src="https://eliteacademymedia.s3.us-east-1.amazonaws.com/qashqool_assets/media/qashqool-logo-dark.svg"
              alt="كشكول"
              className="rounded-sm"
            />
          </a>
          <button
            className="inline-flex items-center p-2 text-sm rounded-lg md:hidden"
            type="button"
            data-bs-toggle="collapse"
            data-bs-target="#navbarText"
            aria-controls="navbarText"
            aria-expanded="false"
            aria-label="Toggle navigation"
          >
            <span className="navbar-toggler-icon"></span>
          </button>
          <div
            id="navbarText"
            className="md:flex md:grow justify-center hidden items-center"
          >
            <ul className="flex flex-col md:flex-row md:mx-auto gap-4 md:gap-6 text-lg justify-between">
              <li>
                <a className="hover:text-primary" href="#home">
                  الرئيسية
                </a>
              </li>
              <li>
                <a className="hover:text-primary" href="#features">
                  المميزات
                </a>
              </li>
              <li>
                <a className="hover:text-primary" href="#pricing">
                  الأسعار
                </a>
              </li>
              <li>
                <a className="hover:text-primary" href="#faq">
                  الأسئلة الشائعة
                </a>
              </li>
            </ul>
            <div className="mt-4 md:mt-0 md:ml-4">
              <Button onClick={() => navigate("/registration")}>
                إشترك مجاناً
              </Button>
            </div>
          </div>
        </div>
      </nav>

      <section id="home" className={`${styles.hero} px-5`}>
        <p className={styles.heroText}>
          منصتك التعليمية{" "}
          <strong>
            <em>بإسمك</em>
          </strong>{" "}
          في{" "}
          <strong>
            <em>5</em>
          </strong>{" "}
          دقايق
        </p>
        <p className={styles.heroSubtext}>
          إشترك الآن مجاناً علي كشكول و أحصل علي منصتك التعليمية الخاصة <br />
          في 5 دقايق .. إعرف التفاصيل و إشترك دلوقتي
          <br />
          <strong>
            <em>كشكول .. سهلة ومش مستاهلة</em>
          </strong>
        </p>
        <div className={styles.heroBtnGroup}>
          <button
            className={`btn ${styles.btn} ${styles.btnPrimary} text-center`}
            onClick={() => navigate("/registration")}
          >
            إشترك مجاناً
          </button>
          <a
            className={`btn ${styles.btn} ${styles.btnPrimary} ${styles.outline} text-center`}
            href="#features-video"
          >
            إعرف المزيد
          </a>
        </div>
      </section>

      <div className={styles.heroMobile}>
        <img
          src="https://eliteacademymedia.s3.us-east-1.amazonaws.com/qashqool_assets/media/hero.webp"
          alt=""
        />
      </div>

      <section id="features-video" className="text-center py-10 px-5">
        <div className="container mx-auto">
          <h2 className={`mb-4 mx-auto text-2xl ${styles.underlineText}`}>
            شاهد كيف يعمل كشكول
          </h2>
          <div className="flex justify-center">
            <div className="w-full max-w-3xl">
              <div className="aspect-w-16 aspect-h-9 shadow-lg rounded-ea">
                <iframe
                  className="rounded-ea w-full h-full"
                  src="https://www.youtube.com/embed/----VIDEO_ID_HERE----"
                  allowFullScreen
                ></iframe>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section id="features" className="py-10 bg-gray-100 px-5">
        <div className="container mx-auto">
          <h2
            className={`text-center mb-6 mx-auto text-2xl ${styles.underlineText}`}
          >
            المميزات
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {features.map((feature, index) => {
              const Icon = feature.icon;
              return (
                <Card
                  key={index}
                  className="hover:shadow-xl transition-all duration-300 hover:-translate-y-1 border-0 shadow-md"
                >
                  <CardHeader className="pb-4">
                    <div className="flex items-center justify-between mb-4">
                      <div
                        className={`w-14 h-14 ${feature.color} rounded-xl flex items-center justify-center`}
                      >
                        <Icon className="w-7 h-7" />
                      </div>
                      <Badge variant="secondary" className="text-xs">
                        {feature.badge}
                      </Badge>
                    </div>
                    <CardTitle className="text-xl font-semibold">
                      {feature.title}
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <CardDescription className="text-gray-600 leading-relaxed">
                      {feature.description}
                    </CardDescription>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      <section id="pricing" className="py-10 bg-white px-5">
        <div className="container mx-auto">
          <h2
            className={`text-center mb-6 mx-auto text-2xl ${styles.underlineText}`}
          >
            الأسعار
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {plans.map((plan, index) => {
              const isSubscribed =
                userProfileData?.subscription_tier === plan.key &&
                !isSubscriptionExpired();
              return (
                <Card
                  key={index}
                  className={`relative ${
                    plan.popular ? "border-2 border-yellow-400 shadow-lg" : ""
                  }`}
                >
                  {plan.popular && (
                    <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
                      <span className="bg-yellow-400 text-yellow-900 px-4 py-1 rounded-full text-sm font-medium">
                        الأكثر مبيعاً
                      </span>
                    </div>
                  )}

                  <CardHeader className="text-center pb-2">
                    <div className="flex justify-center mb-4">{plan.icon}</div>
                    <CardTitle className="text-2xl font-bold">
                      {plan.title}
                    </CardTitle>
                    <div className="mt-4">
                      <span className="text-4xl font-bold">{plan.price}</span>
                    </div>
                  </CardHeader>

                  <CardContent className="space-y-6">
                    <div>
                      <h4 className="font-semibold text-primary mb-3">
                        المميزات
                      </h4>
                      <ul className="space-y-2">
                        {Object.keys(plan.features).map(
                          (feature, featureIndex) => (
                            <li
                              key={featureIndex}
                              className="flex items-start gap-2"
                            >
                              {plan.features[feature] ? (
                                <CircleCheckBig className="size-5 text-green-600" />
                              ) : (
                                <CircleX className="size-5 text-red-500" />
                              )}
                              <span className="text-sm">{feature}</span>
                            </li>
                          )
                        )}
                      </ul>
                    </div>

                    <Button
                      className="w-full"
                      variant="default"
                      disabled={isSubscribed}
                      onClick={registerPopup}
                    >
                      {plan.buttonText}
                    </Button>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      <section id="faq" className="py-10 bg-gray-100 px-5">
        <div className="container mx-auto">
          <h2
            className={`text-center mb-6 mx-auto text-2xl ${styles.underlineText}`}
          >
            أسئلة شائعة
          </h2>
          <Accordion type="single" collapsible>
            {faq.map((q, index) => (
              <AccordionItem value={`item-${index + 1}`}>
                <AccordionTrigger className="text-lg cursor-pointer">
                  {q.question}
                </AccordionTrigger>
                <AccordionContent className="text-base">
                  {q.answer}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>

      <footer className="text-center text-white bg-black py-3">
        <p>© 2025 كشكول. جميع الحقوق محفوظة.</p>
      </footer>
    </>
  );
};

export default LandingPage;